import { getAuthSession } from './authSession';

type AnalyticsPrimitive = string | number | boolean;

type AnalyticsParams = Record<string, AnalyticsPrimitive | null | undefined>;

type TrackEventOptions = {
  dedupeKey?: string;
};

type WorkspaceEventContext = {
  teamId: string;
  projectId: string;
  page: string;
  entityType?: string;
  surface?: 'list' | 'detail';
};

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

const dedupeWindowMs = 1000;
const recentEventMap = new Map<string, number>();

const isBrowser = () => typeof window !== 'undefined';

const buildPayload = (params?: AnalyticsParams) => {
  const payload: Record<string, AnalyticsPrimitive> = {};
  if (!params) {
    return payload;
  }

  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined) {
      continue;
    }
    payload[key] = value;
  }

  return payload;
};

const shouldSkipByDedupe = (dedupeKey?: string) => {
  if (!dedupeKey) {
    return false;
  }

  const now = Date.now();
  const previous = recentEventMap.get(dedupeKey);
  recentEventMap.set(dedupeKey, now);

  if (!previous) {
    return false;
  }

  return now - previous < dedupeWindowMs;
};

const shouldSkipAnalytics = () => {
  return getAuthSession()?.member.isSystemAdmin === true;
};

export const trackEvent = (eventName: string, params?: AnalyticsParams, options?: TrackEventOptions) => {
  if (!isBrowser()) {
    return;
  }

  if (!window.gtag) {
    return;
  }

  if (shouldSkipAnalytics()) {
    return;
  }

  if (shouldSkipByDedupe(options?.dedupeKey)) {
    return;
  }

  window.gtag('event', eventName, buildPayload(params));
};

export const trackPageView = (path: string) => {
  if (!isBrowser()) {
    return;
  }

  if (!window.gtag) {
    return;
  }

  if (shouldSkipAnalytics()) {
    return;
  }

  window.gtag('event', 'page_view', {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
  });
};

export const buildWorkspaceEventParams = (context: WorkspaceEventContext, extra?: AnalyticsParams) => {
  return {
    team_id: context.teamId,
    project_id: context.projectId,
    page: context.page,
    entity_type: context.entityType,
    surface: context.surface,
    ...extra,
  };
};
